<script setup>
import MSheet from "@/components/m/MSheet.vue";
import MIcon from "@/components/m/MIcon.vue";

/* Workspace switcher — lists the companies the user watches and switches the
   active company in the store. `list` items: { id, name, initial, color, ink,
   sub } */
defineProps({
  open: { type: Boolean, default: false },
  list: { type: Array, default: () => [] },
  currentIdx: { type: Number, default: 0 },
  user: { type: Object, default: () => ({ name: "", sub: "" }) },
  // Competitors of the active company, observable in read-only mode.
  // Items: { name, logo, company_id, ... } (raw competitor objects).
  competitors: { type: Array, default: () => [] },
  viewingId: { default: null },
});
const emit = defineEmits([
  "close",
  "pick",
  "logout",
  "profile",
  "view-competitor",
]);
</script>

<template>
  <MSheet :open="open" label="Switch workspace" @close="emit('close')">
    <div
      style="display: flex; align-items: center; gap: 10px; margin: 2px 0 14px"
    >
      <div
        style="
          font-size: 19px;
          font-weight: 800;
          letter-spacing: -0.02em;
          white-space: nowrap;
        "
      >
        Switch workspace
      </div>
      <span class="btm-muted" style="margin-left: auto"
        >{{ list.length }} {{ list.length === 1 ? "brand" : "brands" }}</span
      >
    </div>

    <div style="display: flex; flex-direction: column; gap: 9px">
      <button
        v-for="(w, i) in list"
        :key="w.id"
        :style="{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 13px',
          borderRadius: '16px',
          border:
            '1px solid ' +
            (i === currentIdx ? 'var(--brand-line)' : 'var(--border)'),
          background: i === currentIdx ? 'var(--brand-soft)' : 'var(--surface)',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'var(--ui)',
          width: '100%',
        }"
        @click="emit('pick', i)"
      >
        <span
          class="btm-ava"
          :style="{
            width: '42px',
            height: '42px',
            borderRadius: '13px',
            fontSize: '17px',
            background: w.color,
            color: w.ink,
          }"
          >{{ w.initial }}</span
        >
        <div style="flex: 1; min-width: 0">
          <div style="font-weight: 800; font-size: 15px">{{ w.name }}</div>
          <div v-if="w.sub" class="btm-muted" style="font-size: 12px">
            {{ w.sub }}
          </div>
        </div>
        <span
          :style="{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            flex: '0 0 auto',
            display: 'grid',
            placeItems: 'center',
            background: i === currentIdx ? 'var(--brand)' : 'transparent',
            color: i === currentIdx ? '#2A1C00' : 'transparent',
            border: i === currentIdx ? 'none' : '1.5px solid var(--border-2)',
          }"
        >
          <MIcon v-if="i === currentIdx" name="check" :size="13" :sw="2.8" />
        </span>
      </button>
    </div>

    <!-- Observe competitors (read-only) -->
    <template v-if="competitors.length">
      <div
        class="btm-eyebrow"
        style="
          margin: 18px 2px 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        "
      >
        <MIcon name="competitors" :size="13" />Observe competitors
      </div>
      <div style="display: flex; flex-direction: column; gap: 8px">
        <button
          v-for="comp in competitors"
          :key="comp.link_id ?? comp.company_id"
          type="button"
          :style="{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '11px 13px',
            borderRadius: '14px',
            border:
              '1px solid ' +
              (String(viewingId) === String(comp.company_id)
                ? 'var(--violet)'
                : 'var(--border)'),
            background:
              String(viewingId) === String(comp.company_id)
                ? 'var(--info-soft)'
                : 'var(--surface)',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'var(--ui)',
            width: '100%',
          }"
          @click="emit('view-competitor', comp)"
        >
          <span
            class="btm-ava"
            style="
              width: 34px;
              height: 34px;
              border-radius: 11px;
              font-size: 14px;
              background: var(--violet);
            "
            >{{ (comp.name || "?").charAt(0).toUpperCase() }}</span
          >
          <div style="flex: 1; min-width: 0">
            <div style="font-weight: 700; font-size: 14px">{{ comp.name }}</div>
            <div class="btm-muted" style="font-size: 11.5px">
              Read-only view
            </div>
          </div>
          <MIcon name="external" :size="15" style="opacity: 0.5" />
        </button>
      </div>
    </template>

    <div
      style="
        margin-top: 16px;
        padding: 12px 13px;
        border-radius: 14px;
        background: var(--surface-2);
      "
    >
      <div style="display: flex; align-items: center; gap: 10px">
        <span class="btm-ava" style="background: var(--info)">{{
          (user.name || "U").charAt(0).toUpperCase()
        }}</span>
        <button
          type="button"
          style="
            flex: 1;
            min-width: 0;
            padding: 0;
            border: 0;
            background: transparent;
            text-align: left;
            font-family: var(--ui);
            cursor: pointer;
          "
          @click="emit('profile')"
        >
          <div style="font-weight: 800; font-size: 13.5px; color: var(--text)">
            {{ user.name || "Account" }}
          </div>
          <div class="btm-muted" style="font-size: 11.5px">View profile</div>
        </button>

        <button
          type="button"
          style="
            width: 34px;
            height: 34px;
            border-radius: 50%;
            border: 1px solid var(--border);
            background: var(--surface);
            color: var(--text-2);
            display: grid;
            place-items: center;
            flex: 0 0 auto;
            cursor: pointer;
          "
          aria-label="Log out"
          title="Log out"
          @click="emit('logout')"
        >
          <MIcon name="logout" :size="15" />
        </button>
      </div>
    </div>
  </MSheet>
</template>
